import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api.js';
import { Button } from './Button.js';
import {
  type Corner,
  type ZoneState,
  addCorner,
  clearZone,
  moveCorner,
  removeCorner,
  undo,
  zoneOf,
} from './zoneEdits.js';

/**
 * 416 (#1431, mock-up screen 1): a pickup location's zone, drawn on the map. Click the map to add a corner, drag a corner
 * to move it, click a corner to remove it; Undo walks back one edit; Clear empties the zone. Save sends the corners
 * ([lng, lat]) to `PUT /locations/:id/zone`, which decides whether the shape is usable and says why not. Read-only staff
 * see the zone without the controls. Its own map and its own Save, outside the location form (so Enter or a button here
 * never submits that form).
 */
export interface Zone {
  polygon: Corner[];
  updated_at?: string;
}

const ll = (c: Corner): [number, number] => [c[1], c[0]];
const DEFAULT_CENTER: [number, number] = [3.139, 101.6869];

export function ZoneEditor({
  locationId,
  zone,
  centre,
  canEdit,
  onSaved,
}: {
  locationId: string;
  zone: Zone | null;
  /** The location's own point, where an empty map opens. */
  centre: { latitude: number; longitude: number } | null;
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | undefined>(undefined);
  const layers = useRef<L.Layer[]>([]);
  const [state, setState] = useState<ZoneState>({ corners: zone?.polygon ?? [], history: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Every edit is an updater of the previous state: the map's handlers are bound once, and two clicks before a
  // re-render must both land.
  const editable = useRef(canEdit);
  editable.current = canEdit;
  const change = (edit: (prev: ZoneState) => ZoneState) => {
    setSaved(false);
    setError(null);
    setState(edit);
  };

  useEffect(() => {
    if (!container.current || map.current) return;
    const centreLL: [number, number] = centre
      ? [centre.latitude, centre.longitude]
      : DEFAULT_CENTER;
    const m = L.map(container.current).setView(centreLL, centre ? 15 : 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(m);
    // Bound once, but asks the CURRENT permission: the abilities can load after the map was made (found in the
    // browser: a handler bound only when `canEdit` was already true at that moment never let anyone draw).
    m.on('click', (e: L.LeafletMouseEvent) => {
      if (!editable.current) return;
      change((prev) => addCorner(prev, [e.latlng.lng, e.latlng.lat]));
    });
    map.current = m;
    if (zone && zone.polygon.length >= 3) m.fitBounds(L.polygon(zone.polygon.map(ll)).getBounds());
  });
  useEffect(
    () => () => {
      map.current?.remove();
      map.current = undefined;
    },
    [],
  );

  // Redraw the shape and its corners after every edit. `change` passes updaters, so it needs no dependency.
  // biome-ignore lint/correctness/useExhaustiveDependencies: redraw on the corners and the edit right only
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    for (const layer of layers.current) layer.remove();
    layers.current = [];
    if (state.corners.length >= 2) {
      layers.current.push(
        L.polygon(state.corners.map(ll), { color: '#0070C0', weight: 2, fillOpacity: 0.15 }).addTo(
          m,
        ),
      );
    }
    state.corners.forEach((c, i) => {
      const marker = L.circleMarker(ll(c), {
        radius: 6,
        color: '#0070C0',
        fillColor: '#fff',
        fillOpacity: 1,
        weight: 2,
      }).addTo(m);
      layers.current.push(marker);
    });
    // Moving and removing a corner: an invisible draggable marker per corner (circle markers cannot be dragged).
    if (canEdit) {
      state.corners.forEach((c, i) => {
        const handle = L.marker(ll(c), { draggable: true, opacity: 0 }).addTo(m);
        // The invisible handle sits over the corner, so it takes the corner's click too (a drag fires no click).
        handle.on('click', () => change((prev) => removeCorner(prev, i)));
        handle.on('dragend', () => {
          const p = handle.getLatLng();
          change((prev) => moveCorner(prev, i, [p.lng, p.lat]));
        });
        layers.current.push(handle);
      });
    }
  }, [state, canEdit]);

  const save = zoneOf(state);
  const dirty = state.history.length > 0;

  async function onSave() {
    if (!save.savable || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/locations/${locationId}/zone`, { polygon: save.polygon });
      setState({ corners: state.corners, history: [] });
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The zone could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="zone-editor">
      <div
        ref={container}
        data-testid="zone-map"
        className="relative z-0 h-72 w-full rounded-md border border-hairline"
      />
      {canEdit && (
        <>
          <p className="mt-2 text-xs text-muted">
            Click the map to add a corner · drag a corner to move it · click a corner to remove it.
            At least 3 corners; edges may not cross.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => void onSave()}
              disabled={!save.savable || !dirty}
              loading={saving}
            >
              Save zone
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => change(undo)}
              disabled={!dirty}
            >
              Undo last edit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => change(clearZone)}
              disabled={state.corners.length === 0}
            >
              Clear zone
            </Button>
          </div>
        </>
      )}
      <p data-testid="zone-summary" className="mt-2 text-xs text-muted">
        {state.corners.length === 0
          ? 'No zone: rentals picked up here are not measured.'
          : `${state.corners.length} ${state.corners.length === 1 ? 'corner' : 'corners'}`}
        {!save.savable && ' · add at least 3 corners to save'}
      </p>
      {error && (
        <p role="alert" data-testid="zone-error" className="mt-1 text-sm text-semantic-danger-text">
          {error}
        </p>
      )}
      {saved && (
        <p data-testid="zone-saved" className="mt-1 text-sm text-semantic-success-text">
          Zone saved.
        </p>
      )}
    </div>
  );
}
